"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Link, useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import { Pills, PLAYER_OPTS } from "@/app/components/PlayerCountPills";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
import { imageUrl } from "@/lib/image-url";
import { CONTENT_BRACKETS, normalizeBracket, bracketListParams } from "@/lib/content-brackets";
import { PlayerBadge } from "@/app/components/SupporterBadge";
import { textColor } from "@/lib/character-colors";
function cleanId(id: string): string {
  return id.replace(/^(CHARACTER|CARD|RELIC|ENCOUNTER|EVENT|MONSTER|ACT|POTION)\./, "");
}
function displayName(id: string): string {
  return cleanId(id).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// The compendium uses the in-game canonical name ("The Ironclad") on
// detail pages, but it bloats the leaderboard rows. Strip a leading
// English "The " for the leaderboard display only; localized names
// from non-English locales pass through untouched.
function stripThe(name: string): string {
  return name.replace(/^the\s+/i, "");
}
function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}
function formatTimeShort(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}m`;
}
const CHARACTERS = ["Ironclad", "Silent", "Defect", "Necrobinder", "Regent"] as const;
const CHARACTER_COLORS: Record<string, string> = {
  Ironclad: "var(--color-ironclad)",
  Silent: "var(--color-silent)",
  Defect: "var(--color-defect)",
  Necrobinder: "var(--color-necrobinder)",
  Regent: "var(--color-regent)"
};
interface LeaderboardEntry {
  rank: number;
  run_hash: string;
  username: string;
  character: string;
  ascension: number;
  run_time: number;
  floors_reached: number;
}
interface CharacterNameRow {
  id: string;
  name: string;
}
interface BrowseRun {
  run_hash: string;
  character: string;
  ascension: number;
  win: boolean;
  was_abandoned?: boolean;
  username?: string;
  deck_size: number;
  relic_count: number;
  floors_reached: number;
  run_time: number;
}
type Tab = "fastest" | "highest_ascension" | "browse";
type Mode = "" | "1" | "2" | "3" | "4";
// `gameMode` mirrors the backend `game_mode` column. Empty string = no
// filter (all modes). Default is "standard" since custom-seed and daily
// runs aren't time-comparable to the canonical ladder.
type GameMode = "" | "standard" | "daily" | "daily_today" | "custom";
function tabFromParam(value: string | null): Tab {
  if (value === "browse" || value === "highest_ascension") return value;
  return "fastest";
}
function modeFromParam(value: string | null): Mode {
  if (value === "2" || value === "3" || value === "4") return value;
  // Legacy deep links: ?mode=all|multi -> all runs; single|1 -> solo.
  if (value === "all" || value === "multi" || value === "") return "";
  return "1"; // default: solo (SP/MP times aren't comparable)
}
function gameModeFromParam(value: string | null): GameMode {
  if (value === "daily" || value === "daily_today" || value === "custom" || value === "") return value;
  if (value === "all") return "";
  return "standard";
}
export default function LeaderboardBrowseClient() {
  const $ = _c(205);
  const bp = useBetaPrefix();
  const lang = useGameLocale();
  const t = useT();
  const router = useRouter();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => tabFromParam(searchParams.get("tab"));
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [tab, setTab] = useState(t0);
  let t1;
  if ($[2] !== searchParams) {
    t1 = () => modeFromParam(searchParams.get("mode"));
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [mode, setMode] = useState(t1);
  let t2;
  if ($[4] !== searchParams) {
    t2 = () => gameModeFromParam(searchParams.get("game_mode"));
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [gameMode, setGameMode] = useState(t2);
  let t3;
  if ($[6] !== searchParams) {
    t3 = () => searchParams.get("character") || "";
    $[6] = searchParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const [lbChar, setLbChar] = useState(t3);
  let t4;
  if ($[8] !== searchParams) {
    t4 = () => {
      if (tabFromParam(searchParams.get("tab")) === "browse") {
        return 1;
      }
      const n = Number(searchParams.get("page"));
      return Number.isInteger(n) && n > 1 ? n : 1;
    };
    $[8] = searchParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [lbPage, setLbPage] = useState(t4);
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const [lbEntries, setLbEntries] = useState(t5);
  const [, setLbTotal] = useState(0);
  const [lbTotalPages, setLbTotalPages] = useState(0);
  const [lbLoading, setLbLoading] = useState(false);
  let t6;
  if ($[11] !== searchParams) {
    t6 = () => searchParams.get("browse_character") || "";
    $[11] = searchParams;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const [browseChar, setBrowseChar] = useState(t6);
  let t7;
  if ($[13] !== searchParams) {
    t7 = () => searchParams.get("win") || "";
    $[13] = searchParams;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const [browseWin, setBrowseWin] = useState(t7);
  let t8;
  if ($[15] !== searchParams) {
    t8 = () => searchParams.get("username") || "";
    $[15] = searchParams;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const [browseUser, setBrowseUser] = useState(t8);
  let t9;
  if ($[17] !== searchParams) {
    t9 = () => searchParams.get("seed") || "";
    $[17] = searchParams;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const [browseSeed, setBrowseSeed] = useState(t9);
  let t10;
  if ($[19] !== searchParams) {
    t10 = () => searchParams.get("build_id") || "";
    $[19] = searchParams;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const [browseBuildId, setBrowseBuildId] = useState(t10);
  let t11;
  if ($[21] !== searchParams) {
    t11 = () => normalizeBracket(searchParams.get("bracket"));
    $[21] = searchParams;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const [browseBracket, setBrowseBracket] = useState(t11);
  let t12;
  if ($[23] !== searchParams) {
    t12 = () => {
      const v = searchParams.get("sort");
      return v === "time_asc" || v === "time_desc" || v === "ascension_desc" ? v : "date";
    };
    $[23] = searchParams;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const [browseSort, setBrowseSort] = useState(t12);
  let t13;
  if ($[25] !== searchParams) {
    t13 = () => {
      if (tabFromParam(searchParams.get("tab")) !== "browse") {
        return 1;
      }
      const n_0 = Number(searchParams.get("page"));
      return Number.isInteger(n_0) && n_0 > 1 ? n_0 : 1;
    };
    $[25] = searchParams;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const [browsePage, setBrowsePage] = useState(t13);
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const [runList, setRunList] = useState(t14);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browseTotalPages, setBrowseTotalPages] = useState(0);
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [];
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const [versions, setVersions] = useState(t15);
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {};
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const [charNames, setCharNames] = useState(t16);
  let t17;
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      fetch(`${API}/api/runs/versions`).then(_temp).then(data => {
        const filtered = (data.versions || []).filter(_temp2).sort(_temp3);
        setVersions(filtered);
      }).catch(_temp4);
    };
    t18 = [];
    $[30] = t17;
    $[31] = t18;
  } else {
    t17 = $[30];
    t18 = $[31];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[32] !== lang) {
    t19 = () => {
      fetch(`${API}/api/characters?lang=${lang}`).then(_temp5).then(data_0 => {
        const m = {};
        for (const c of data_0) {
          m[c.id.toUpperCase()] = c.name;
        }
        setCharNames(m);
      }).catch(_temp6);
    };
    t20 = [lang];
    $[32] = lang;
    $[33] = t19;
    $[34] = t20;
  } else {
    t19 = $[33];
    t20 = $[34];
  }
  useEffect(t19, t20);
  let t21;
  if ($[35] !== charNames) {
    t21 = function charName(id) {
      return charNames[id.toUpperCase()] ?? displayName(`CHARACTER.${id}`);
    };
    $[35] = charNames;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  const charName = t21;
  let t22;
  let t23;
  if ($[37] !== browseBracket || $[38] !== browseBuildId || $[39] !== browseChar || $[40] !== browsePage || $[41] !== browseSeed || $[42] !== browseSort || $[43] !== browseUser || $[44] !== browseWin || $[45] !== gameMode || $[46] !== lbChar || $[47] !== lbPage || $[48] !== mode || $[49] !== tab) {
    t22 = () => {
      const params = new URLSearchParams();
      if (tab !== "fastest") {
        params.set("tab", tab);
      }
      if (mode) {
        params.set("mode", mode);
      }
      if (gameMode && gameMode !== "standard") {
        params.set("game_mode", gameMode);
      }
      if (lbChar) {
        params.set("character", lbChar);
      }
      if (browseChar) {
        params.set("browse_character", browseChar);
      }
      if (browseWin) {
        params.set("win", browseWin);
      }
      if (browseUser) {
        params.set("username", browseUser);
      }
      if (browseSeed) {
        params.set("seed", browseSeed);
      }
      if (browseBuildId) {
        params.set("build_id", browseBuildId);
      }
      if (browseBracket !== "all") {
        params.set("bracket", browseBracket);
      }
      if (browseSort !== "date") {
        params.set("sort", browseSort);
      }
      const activePage = tab === "browse" ? browsePage : lbPage;
      if (activePage > 1) {
        params.set("page", String(activePage));
      }
      const qs = params.toString();
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      if (url === window.location.pathname + window.location.search) {
        return;
      }
      window.history.replaceState(null, "", url);
    };
    t23 = [tab, mode, gameMode, lbChar, browseChar, browseWin, browseUser, browseSeed, browseBuildId, browseBracket, browseSort, lbPage, browsePage];
    $[37] = browseBracket;
    $[38] = browseBuildId;
    $[39] = browseChar;
    $[40] = browsePage;
    $[41] = browseSeed;
    $[42] = browseSort;
    $[43] = browseUser;
    $[44] = browseWin;
    $[45] = gameMode;
    $[46] = lbChar;
    $[47] = lbPage;
    $[48] = mode;
    $[49] = tab;
    $[50] = t22;
    $[51] = t23;
  } else {
    t22 = $[50];
    t23 = $[51];
  }
  useEffect(t22, t23);
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setLbPage(1);
    };
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== browseBracket || $[54] !== browseBuildId || $[55] !== gameMode || $[56] !== lbChar || $[57] !== mode || $[58] !== tab) {
    t25 = [tab, lbChar, mode, gameMode, browseBracket, browseBuildId];
    $[53] = browseBracket;
    $[54] = browseBuildId;
    $[55] = gameMode;
    $[56] = lbChar;
    $[57] = mode;
    $[58] = tab;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[60] !== browseBracket || $[61] !== browseBuildId || $[62] !== gameMode || $[63] !== lbChar || $[64] !== lbPage || $[65] !== mode || $[66] !== tab) {
    t26 = () => {
      if (tab === "browse") {
        return;
      }
      setLbLoading(true);
      const params_0 = new URLSearchParams();
      params_0.set("category", tab);
      if (mode) {
        params_0.set("players", mode);
      }
      if (gameMode === "daily_today") {
        params_0.set("game_mode", "daily");
        params_0.set("today", "true");
      } else {
        if (gameMode) {
          params_0.set("game_mode", gameMode);
        }
      }
      if (lbChar) {
        params_0.set("character", lbChar);
      }
      const bp_0 = bracketListParams(browseBracket);
      if (bp_0.ascension_min) {
        params_0.set("ascension_min", String(bp_0.ascension_min));
      }
      if (bp_0.winrate_min) {
        params_0.set("winrate_min", String(bp_0.winrate_min));
      }
      if (browseBuildId) {
        params_0.set("build_id", browseBuildId);
      }
      params_0.set("page", String(lbPage));
      params_0.set("limit", "20");
      fetch(`${API}/api/runs/leaderboard?${params_0}`).then(_temp7).then(data_1 => {
        const rows = (data_1.runs || []) as Omit<LeaderboardEntry, "rank">[];
        const offset = (Math.max(lbPage, 1) - 1) * 20;
        setLbEntries(rows.map((r_2, i) => ({
          ...r_2,
          rank: offset + i + 1
        })));
        setLbTotal(data_1.total || 0);
        setLbTotalPages(data_1.total_pages || 0);
      }).catch(() => {
        setLbEntries([]);
        setLbTotal(0);
        setLbTotalPages(0);
      }).finally(() => setLbLoading(false));
    };
    t27 = [tab, lbChar, lbPage, mode, gameMode, browseBracket, browseBuildId];
    $[60] = browseBracket;
    $[61] = browseBuildId;
    $[62] = gameMode;
    $[63] = lbChar;
    $[64] = lbPage;
    $[65] = mode;
    $[66] = tab;
    $[67] = t26;
    $[68] = t27;
  } else {
    t26 = $[67];
    t27 = $[68];
  }
  useEffect(t26, t27);
  let t28;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      setBrowsePage(1);
    };
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== browseBracket || $[71] !== browseBuildId || $[72] !== browseChar || $[73] !== browseSeed || $[74] !== browseSort || $[75] !== browseUser || $[76] !== browseWin || $[77] !== gameMode || $[78] !== mode) {
    t29 = [browseChar, browseWin, browseUser, browseSeed, browseBuildId, browseSort, browseBracket, mode, gameMode];
    $[70] = browseBracket;
    $[71] = browseBuildId;
    $[72] = browseChar;
    $[73] = browseSeed;
    $[74] = browseSort;
    $[75] = browseUser;
    $[76] = browseWin;
    $[77] = gameMode;
    $[78] = mode;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[80] !== browseBracket || $[81] !== browseBuildId || $[82] !== browseChar || $[83] !== browsePage || $[84] !== browseSeed || $[85] !== browseSort || $[86] !== browseUser || $[87] !== browseWin || $[88] !== gameMode || $[89] !== mode || $[90] !== tab) {
    t30 = () => {
      if (tab !== "browse") {
        return;
      }
      const params_1 = new URLSearchParams();
      if (mode) {
        params_1.set("players", mode);
      }
      if (gameMode === "daily_today") {
        params_1.set("game_mode", "daily");
        params_1.set("today", "true");
      } else {
        if (gameMode) {
          params_1.set("game_mode", gameMode);
        }
      }
      if (browseChar) {
        params_1.set("character", browseChar);
      }
      if (browseWin) {
        params_1.set("win", browseWin);
      }
      if (browseUser) {
        params_1.set("username", browseUser);
      }
      if (browseSeed) {
        params_1.set("seed", browseSeed);
      }
      if (browseBuildId) {
        params_1.set("build_id", browseBuildId);
      }
      if (browseSort) {
        params_1.set("sort", browseSort);
      }
      const bp_1 = bracketListParams(browseBracket);
      if (bp_1.ascension_min) {
        params_1.set("ascension_min", String(bp_1.ascension_min));
      }
      if (bp_1.winrate_min) {
        params_1.set("winrate_min", String(bp_1.winrate_min));
      }
      params_1.set("page", String(browsePage));
      fetch(`${API}/api/runs/list?${params_1}`).then(_temp8).then(data_2 => {
        setRunList(data_2.runs || []);
        setBrowseTotal(data_2.total || 0);
        setBrowseTotalPages(data_2.total_pages || 0);
      }).catch(_temp9);
    };
    t31 = [tab, browseChar, browseWin, browseUser, browseSeed, browseBuildId, browseSort, browseBracket, browsePage, mode, gameMode];
    $[80] = browseBracket;
    $[81] = browseBuildId;
    $[82] = browseChar;
    $[83] = browsePage;
    $[84] = browseSeed;
    $[85] = browseSort;
    $[86] = browseUser;
    $[87] = browseWin;
    $[88] = gameMode;
    $[89] = mode;
    $[90] = tab;
    $[91] = t30;
    $[92] = t31;
  } else {
    t30 = $[91];
    t31 = $[92];
  }
  useEffect(t30, t31);
  let t32;
  if ($[93] !== t) {
    t32 = t("Fastest Wins");
    $[93] = t;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== t) {
    t33 = t("Fastest");
    $[95] = t;
    $[96] = t33;
  } else {
    t33 = $[96];
  }
  let t34;
  if ($[97] !== t32 || $[98] !== t33) {
    t34 = {
      key: "fastest",
      label: t32,
      shortLabel: t33
    };
    $[97] = t32;
    $[98] = t33;
    $[99] = t34;
  } else {
    t34 = $[99];
  }
  let t35;
  if ($[100] !== t) {
    t35 = t("Highest Ascension");
    $[100] = t;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  let t36;
  if ($[102] !== t) {
    t36 = t("Ascension");
    $[102] = t;
    $[103] = t36;
  } else {
    t36 = $[103];
  }
  let t37;
  if ($[104] !== t35 || $[105] !== t36) {
    t37 = {
      key: "highest_ascension",
      label: t35,
      shortLabel: t36
    };
    $[104] = t35;
    $[105] = t36;
    $[106] = t37;
  } else {
    t37 = $[106];
  }
  let t38;
  if ($[107] !== t34 || $[108] !== t37) {
    t38 = [t34, t37];
    $[107] = t34;
    $[108] = t37;
    $[109] = t38;
  } else {
    t38 = $[109];
  }
  const TABS = t38;
  const isLeaderboard = tab !== "browse";
  let t39;
  if ($[110] !== t) {
    t39 = t("Leaderboards");
    $[110] = t;
    $[111] = t39;
  } else {
    t39 = $[111];
  }
  let t40;
  if ($[112] !== t39) {
    t40 = <h1 className="text-3xl font-bold text-[var(--accent-gold)]">{t39}</h1>;
    $[112] = t39;
    $[113] = t40;
  } else {
    t40 = $[113];
  }
  const t41 = `${bp}/runs`;
  let t42;
  if ($[114] !== t) {
    t42 = t("Browse Runs");
    $[114] = t;
    $[115] = t42;
  } else {
    t42 = $[115];
  }
  let t43;
  if ($[116] !== t41 || $[117] !== t42) {
    t43 = <Link prefetch={false} href={t41} className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">{t42} →</Link>;
    $[116] = t41;
    $[117] = t42;
    $[118] = t43;
  } else {
    t43 = $[118];
  }
  let t44;
  if ($[119] !== t40 || $[120] !== t43) {
    t44 = <div className="flex items-end justify-between mb-4">{t40}{t43}</div>;
    $[119] = t40;
    $[120] = t43;
    $[121] = t44;
  } else {
    t44 = $[121];
  }
  let t45;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = v_1 => setMode(v_1 as Mode);
    $[122] = t45;
  } else {
    t45 = $[122];
  }
  let t46;
  if ($[123] !== t) {
    t46 = t("Filter by player count");
    $[123] = t;
    $[124] = t46;
  } else {
    t46 = $[124];
  }
  let t47;
  if ($[125] !== mode || $[126] !== t46) {
    t47 = <Pills options={PLAYER_OPTS} value={mode} onChange={t45} ariaLabel={t46} />;
    $[125] = mode;
    $[126] = t46;
    $[127] = t47;
  } else {
    t47 = $[127];
  }
  let t48;
  if ($[128] !== t) {
    t48 = t("Standard");
    $[128] = t;
    $[129] = t48;
  } else {
    t48 = $[129];
  }
  let t49;
  if ($[130] !== t48) {
    t49 = {
      value: "standard" as GameMode,
      label: t48
    };
    $[130] = t48;
    $[131] = t49;
  } else {
    t49 = $[131];
  }
  let t50;
  if ($[132] !== t) {
    t50 = t("Daily");
    $[132] = t;
    $[133] = t50;
  } else {
    t50 = $[133];
  }
  let t51;
  if ($[134] !== t50) {
    t51 = {
      value: "daily" as GameMode,
      label: t50
    };
    $[134] = t50;
    $[135] = t51;
  } else {
    t51 = $[135];
  }
  let t52;
  if ($[136] !== t) {
    t52 = t("Custom");
    $[136] = t;
    $[137] = t52;
  } else {
    t52 = $[137];
  }
  let t53;
  if ($[138] !== t52) {
    t53 = {
      value: "custom" as GameMode,
      label: t52
    };
    $[138] = t52;
    $[139] = t53;
  } else {
    t53 = $[139];
  }
  let t54;
  if ($[140] !== t) {
    t54 = t("All Modes");
    $[140] = t;
    $[141] = t54;
  } else {
    t54 = $[141];
  }
  let t55;
  if ($[142] !== t54) {
    t55 = {
      value: "" as GameMode,
      label: t54
    };
    $[142] = t54;
    $[143] = t55;
  } else {
    t55 = $[143];
  }
  let t56;
  if ($[144] !== t49 || $[145] !== t51 || $[146] !== t53 || $[147] !== t55) {
    t56 = [t49, t51, t53, t55];
    $[144] = t49;
    $[145] = t51;
    $[146] = t53;
    $[147] = t55;
    $[148] = t56;
  } else {
    t56 = $[148];
  }
  let t57;
  if ($[149] !== gameMode || $[150] !== t56) {
    t57 = <div className="inline-flex gap-1 p-1 rounded-lg bg-[var(--bg-card)] border border-[var(--border-subtle)]">{t56.map(t58 => {
        const {
          value,
          label
        } = t58;
        return <button key={value || "all"} onClick={() => {
          if (gameMode === "daily_today" && value !== "daily") {
            setGameMode(value);
          } else {
            setGameMode(value);
          }
        }} className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${gameMode === value || value === "daily" && gameMode === "daily_today" ? "bg-[var(--accent-gold)] text-[var(--bg-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}>{label}</button>;
      })}</div>;
    $[149] = gameMode;
    $[150] = t56;
    $[151] = t57;
  } else {
    t57 = $[151];
  }
  let t58;
  if ($[152] !== browseBracket || $[153] !== t) {
    t58 = CONTENT_BRACKETS.map(b_0 => <button key={b_0.key} onClick={() => setBrowseBracket(b_0.key)} className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${browseBracket === b_0.key ? "bg-[var(--accent-gold)] text-[var(--bg-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}>{t(b_0.label)}</button>);
    $[152] = browseBracket;
    $[153] = t;
    $[154] = t58;
  } else {
    t58 = $[154];
  }
  let t59;
  if ($[155] !== t58) {
    t59 = <div className="inline-flex flex-wrap gap-1 p-1 rounded-lg bg-[var(--bg-card)] border border-[var(--border-subtle)]">{t58}</div>;
    $[155] = t58;
    $[156] = t59;
  } else {
    t59 = $[156];
  }
  let t60;
  if ($[157] !== gameMode || $[158] !== t) {
    t60 = (gameMode === "daily" || gameMode === "daily_today") && <button onClick={() => setGameMode(gameMode === "daily_today" ? "daily" : "daily_today")} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${gameMode === "daily_today" ? "bg-[var(--accent-gold)] text-[var(--bg-primary)] border-[var(--accent-gold)]" : "bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}>{t("Today")}</button>;
    $[157] = gameMode;
    $[158] = t;
    $[159] = t60;
  } else {
    t60 = $[159];
  }
  let t61;
  if ($[160] !== t47 || $[161] !== t57 || $[162] !== t59 || $[163] !== t60) {
    t61 = <div className="flex flex-wrap items-center gap-3 mb-6">{t47}{t57}{t59}{t60}</div>;
    $[160] = t47;
    $[161] = t57;
    $[162] = t59;
    $[163] = t60;
    $[164] = t61;
  } else {
    t61 = $[164];
  }
  let t62;
  if ($[165] !== TABS || $[166] !== tab) {
    t62 = <div className="flex gap-1 mb-6 border-b border-[var(--border-subtle)]">{TABS.map(tb => <button key={tb.key} onClick={() => setTab(tb.key)} className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${tab === tb.key ? "border-[var(--accent-gold)] text-[var(--accent-gold)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]"}`}><span className="sm:hidden">{tb.shortLabel}</span><span className="hidden sm:inline">{tb.label}</span></button>)}</div>;
    $[165] = TABS;
    $[166] = tab;
    $[167] = t62;
  } else {
    t62 = $[167];
  }
  let t63;
  if ($[168] !== bp || $[169] !== browseBuildId || $[170] !== charName || $[171] !== isLeaderboard || $[172] !== lang || $[173] !== lbChar || $[174] !== lbEntries || $[175] !== lbLoading || $[176] !== lbPage || $[177] !== lbTotalPages || $[178] !== router || $[179] !== t || $[180] !== versions) {
    t63 = isLeaderboard && <><div className="flex flex-wrap gap-2 mb-4"><button onClick={() => setLbChar("")} aria-label={t("All")} className={`text-sm h-9 sm:h-auto sm:px-3 sm:py-1.5 px-3 rounded-lg border transition-colors ${lbChar === "" ? "bg-[var(--accent-gold)] text-[var(--bg-primary)] border-[var(--accent-gold)]" : "bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}>{t("All")}</button>{CHARACTERS.map(ch => <button key={ch} onClick={() => setLbChar(ch.toUpperCase())} title={charName(ch)} aria-label={charName(ch)} className={`flex items-center justify-center gap-1.5 h-9 sm:h-auto px-2 sm:px-3 sm:py-1.5 rounded-lg border transition-colors ${lbChar === ch.toUpperCase() ? "border-transparent text-[var(--bg-primary)]" : "bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`} style={lbChar === ch.toUpperCase() ? {
          backgroundColor: CHARACTER_COLORS[ch],
          borderColor: CHARACTER_COLORS[ch]
        } : undefined}><img src={imageUrl(`/static/images/characters/character_icon_${ch.toLowerCase()}.webp`)} alt="" aria-hidden={true} className="w-6 h-6 object-contain flex-shrink-0" loading="lazy" crossOrigin="anonymous" /><span className="hidden sm:inline text-sm">{stripThe(charName(ch))}</span></button>)}{versions.length > 0 && <select value={browseBuildId} onChange={e => setBrowseBuildId(e.target.value)} aria-label={t("Game version")} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]"><option value="">{t("All Versions")}</option>{versions.map(_temp10)}</select>}</div>{lbLoading ? <p className="text-center py-8 text-[var(--text-muted)]">{t("Loading...")}</p> : lbEntries.length === 0 ? <p className="text-center py-8 text-[var(--text-muted)]">{t("No leaderboard entries found.")}</p> : <><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-[var(--border-subtle)]"><th className="text-left py-2 px-2 sm:px-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{t("Rank")}</th><th className="hidden sm:table-cell text-left py-2 px-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{t("Player")}</th><th className="text-left py-2 px-2 sm:px-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{t("Character")}</th><th className="text-left py-2 px-2 sm:px-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{t("Asc")}</th><th className="text-left py-2 px-2 sm:px-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{t("Time")}</th><th className="hidden sm:table-cell text-left py-2 px-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{t("Floors")}</th><th className="py-2 px-2 sm:px-3" /></tr></thead><tbody>{lbEntries.map(entry => {
                const englishName = displayName(`CHARACTER.${entry.character}`);
                const localizedName = stripThe(charName(entry.character));
                const charColor = CHARACTER_COLORS[englishName] || "var(--text-primary)";
                return <tr key={entry.run_hash} onClick={() => router.push(`${bp}/runs/${entry.run_hash}`)} className="border-b border-[var(--border-subtle)] hover:bg-[var(--bg-card-hover)] transition-colors cursor-pointer"><td className="py-2.5 px-2 sm:px-3 font-medium text-[var(--accent-gold)]">#{entry.rank}</td><td className="hidden sm:table-cell py-2.5 px-3 text-[var(--text-primary)] truncate max-w-[10rem]">{entry.username || t("Anonymous")}<PlayerBadge username={entry.username} className="ml-1" /></td><td className="py-2.5 px-2 sm:px-3" style={{
                    color: textColor(charColor)
                  }}><span className="sm:hidden">{localizedName.slice(0, 3)}</span><span className="hidden sm:inline">{localizedName}</span></td><td className="py-2.5 px-2 sm:px-3 text-[var(--text-secondary)]">A{entry.ascension}</td><td className="py-2.5 px-2 sm:px-3 text-[var(--text-secondary)] whitespace-nowrap">{formatTime(entry.run_time)}</td><td className="hidden sm:table-cell py-2.5 px-3 text-[var(--text-secondary)]">{entry.floors_reached}</td><td className="py-2.5 px-2 sm:px-3"><Link prefetch={false} href={`${bp}/runs/${entry.run_hash}`} onClick={_temp11} className="text-[var(--accent-gold)] hover:underline text-xs" title={t("View run")} aria-label={t("View run details")}>↗</Link></td></tr>;
              })}</tbody></table></div>{lbTotalPages > 1 && <Pagination page={lbPage} totalPages={lbTotalPages} onPageChange={setLbPage} lang={lang} />}</>}</>;
    $[168] = bp;
    $[169] = browseBuildId;
    $[170] = charName;
    $[171] = isLeaderboard;
    $[172] = lang;
    $[173] = lbChar;
    $[174] = lbEntries;
    $[175] = lbLoading;
    $[176] = lbPage;
    $[177] = lbTotalPages;
    $[178] = router;
    $[179] = t;
    $[180] = versions;
    $[181] = t63;
  } else {
    t63 = $[181];
  }
  let t64;
  if ($[182] !== bp || $[183] !== browseBuildId || $[184] !== browseChar || $[185] !== browsePage || $[186] !== browseSeed || $[187] !== browseSort || $[188] !== browseTotal || $[189] !== browseTotalPages || $[190] !== browseUser || $[191] !== browseWin || $[192] !== charName || $[193] !== lang || $[194] !== runList || $[195] !== t || $[196] !== tab || $[197] !== versions) {
    t64 = tab === "browse" && <><div className="grid grid-cols-2 gap-2 mb-4 sm:flex sm:flex-wrap"><select value={browseChar} onChange={e_1 => setBrowseChar(e_1.target.value)} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] w-full sm:w-auto"><option value="">{t("All Characters")}</option>{CHARACTERS.map(ch_0 => <option key={ch_0} value={ch_0.toUpperCase()}>{stripThe(charName(ch_0))}</option>)}</select><select value={browseWin} onChange={e_2 => setBrowseWin(e_2.target.value)} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] w-full sm:w-auto"><option value="">{t("All Runs")}</option><option value="true">{t("Wins")}</option><option value="false">{t("Losses")}</option></select><input type="text" value={browseUser} onChange={e_3 => setBrowseUser(e_3.target.value)} placeholder={t("Username...")} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] w-full sm:w-44" /><input type="text" value={browseSeed} onChange={e_4 => setBrowseSeed(e_4.target.value)} placeholder={t("Seed...")} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] w-full sm:w-36" />{versions.length > 0 && <select value={browseBuildId} onChange={e_5 => setBrowseBuildId(e_5.target.value)} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] w-full sm:w-auto"><option value="">{t("All Versions")}</option>{versions.map(_temp12)}</select>}<select value={browseSort} onChange={e_6 => setBrowseSort(e_6.target.value)} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] w-full sm:w-auto"><option value="date">{t("Newest")}</option><option value="time_asc">{t("Fastest")}</option><option value="time_desc">{t("Slowest")}</option><option value="ascension_desc">{t("Highest Asc")}</option></select></div><p className="text-xs text-[var(--text-muted)] mb-3">{browseTotal} {t("runs total")}</p>{runList.length === 0 ? <p className="text-center py-8 text-[var(--text-muted)]">{t("No runs found.")}</p> : <><div className="space-y-2">{runList.map(r_4 => <Link prefetch={false} key={r_4.run_hash} href={`${bp}/runs/${r_4.run_hash}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] px-3 sm:px-4 py-3 hover:bg-[var(--bg-card-hover)] transition-colors"><div className="flex items-center gap-2 sm:gap-3 min-w-0"><span className={`text-sm font-medium shrink-0 ${r_4.win ? "text-[var(--color-silent)]" : "text-[var(--color-ironclad)]"}`}>{r_4.win ? "W" : r_4.was_abandoned ? "A" : "L"}</span><span className="text-sm text-[var(--text-primary)] truncate">{stripThe(charName(r_4.character))}</span><span className="text-xs text-[var(--text-muted)] shrink-0">A{r_4.ascension}</span>{r_4.username && <span className="text-xs text-[var(--accent-gold)] truncate">{r_4.username}</span>}<PlayerBadge username={r_4.username} /></div><div className="flex items-center gap-3 sm:gap-4 text-xs text-[var(--text-muted)] shrink-0"><span className="hidden sm:inline">{t("{n} cards", {
                  n: r_4.deck_size
                })}</span><span className="hidden sm:inline">{t("{n} relics", {
                  n: r_4.relic_count
                })}</span><span>{r_4.floors_reached}f</span><span>{formatTimeShort(r_4.run_time)}</span></div></Link>)}</div>{browseTotalPages > 1 && <Pagination page={browsePage} totalPages={browseTotalPages} onPageChange={setBrowsePage} lang={lang} />}</>}</>;
    $[182] = bp;
    $[183] = browseBuildId;
    $[184] = browseChar;
    $[185] = browsePage;
    $[186] = browseSeed;
    $[187] = browseSort;
    $[188] = browseTotal;
    $[189] = browseTotalPages;
    $[190] = browseUser;
    $[191] = browseWin;
    $[192] = charName;
    $[193] = lang;
    $[194] = runList;
    $[195] = t;
    $[196] = tab;
    $[197] = versions;
    $[198] = t64;
  } else {
    t64 = $[198];
  }
  let t65;
  if ($[199] !== t44 || $[200] !== t61 || $[201] !== t62 || $[202] !== t63 || $[203] !== t64) {
    t65 = <div className="mx-auto max-w-[1400px] px-3 sm:px-5 py-6">{t44}{t61}{t62}{t63}{t64}</div>;
    $[199] = t44;
    $[200] = t61;
    $[201] = t62;
    $[202] = t63;
    $[203] = t64;
    $[204] = t65;
  } else {
    t65 = $[204];
  }
  return t65;
}
function _temp12(v_3) {
  return <option key={v_3} value={v_3}>{v_3}</option>;
}
function _temp11(e_0) {
  return e_0.stopPropagation();
}
function _temp10(v_2) {
  return <option key={v_2} value={v_2}>{v_2}</option>;
}
function _temp9() {}
function _temp8(r_3) {
  return r_3.ok ? r_3.json() : {
    runs: [],
    total: 0,
    total_pages: 0
  };
}
function _temp7(r_1) {
  return r_1.ok ? r_1.json() : {
    runs: [],
    total: 0,
    total_pages: 0
  };
}
function _temp6() {}
function _temp5(r_0) {
  return r_0.ok ? r_0.json() : [];
}
function _temp4() {}
function _temp3(a, b) {
  return b.localeCompare(a, undefined, {
    numeric: true
  });
}
function _temp2(v_0) {
  return !v_0.toLowerCase().includes("nonreleased");
}
function _temp(r) {
  return r.ok ? r.json() : {
    versions: []
  };
}
function Pagination(t0) {
  const $ = _c(31);
  const {
    page,
    totalPages,
    onPageChange
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== onPageChange || $[1] !== page) {
    t1 = () => onPageChange(page - 1);
    $[0] = onPageChange;
    $[1] = page;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = page <= 1;
  let t3;
  if ($[3] !== t) {
    t3 = t("Prev");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = <button onClick={t1} disabled={t2} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">← {t3}</button>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("Page");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("of");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== page || $[14] !== t5 || $[15] !== t6 || $[16] !== totalPages) {
    t7 = <span className="text-xs text-[var(--text-muted)]">{t5} {page} {t6} {totalPages}</span>;
    $[13] = page;
    $[14] = t5;
    $[15] = t6;
    $[16] = totalPages;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== onPageChange || $[19] !== page) {
    t8 = () => onPageChange(page + 1);
    $[18] = onPageChange;
    $[19] = page;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = page >= totalPages;
  let t10;
  if ($[21] !== t) {
    t10 = t("Next");
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8 || $[25] !== t9) {
    t11 = <button onClick={t8} disabled={t9} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">{t10} →</button>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t4 || $[29] !== t7) {
    t12 = <div className="flex items-center justify-center gap-2 mt-4">{t4}{t7}{t11}</div>;
    $[27] = t11;
    $[28] = t4;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}